import React, {useEffect, useMemo, useState} from 'react';
import {Button, Card, Col, Flex, Row, Space, Spin, Tag, Tooltip, Typography} from 'antd';
import {
  BankOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  RightOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {useDispatch, useIntl} from 'umi';
import {ServiceRequestModel} from '@/typings/models/ServiceRequest';
import {BranchActionTag, LeadActionTag} from '@/components/ActionTag';
import AppointmentTimeEditor from '@/components/Kanban/ServiceRequests/AppointmentTimeEditor';
import AppointmentTasks from '@/components/Kanban/ServiceRequests/AppointmentTasks';
import ServiceRequestAction from '@/components/Lead/ServiceRequestAction';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import EditableTextField from '@/components/Lead/EditableTextField';
import CopyableId from '@/components/CopyableId';

import {F} from '@/utils/intl';

import styles from '@/components/Kanban/ServiceRequests/index.less';
import ServiceRequestStatusTag from '@/components/Lead/ServiceRequestStatusTag';
import {useNavigate} from '@@/exports';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import isEmpty from 'lodash/isEmpty';
import {setExtraFieldValueRequest} from '@/services/api/extraFieldType';
import {editServiceRequestRequest} from '@/services/api/serviceRequest';
import {ExtraFieldTypeModel} from '@/typings/models/ExtraFields';
import InfoCard from '@/components/ProjectDetails/Cards/InfoCard';
import ServiceRequestAssignee from '@/components/Lead/ServiceRequestAssignee';
import BranchServiceSelect from '@/components/Location/BranchServiceSelect';
import {useServiceRequestPermissions} from '@/utils/hooks';
import {getServiceRequestAssignee, isServiceRequestStatusEditable} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {confirmIfAgentServiceMismatch} from '@/utils/agentServiceMismatch';
import {describeRecurring} from '@/utils/recurrence';

const { Text } = Typography;

interface ServiceRequestDescriptionsProps {
  appointment: ServiceRequestModel;
  action: string;
  loading?: boolean;
  onClose?: () => void;
  onRefresh?: () => void;
  /**
   * Called after a footer action (delete, cancel, …) completes. Falls back to
   * `onRefresh` — pass it separately when re-fetching the entity is wrong for
   * actions that remove it (e.g. close the drawer instead).
   */
  onActionEnd?: () => void;
}

const APPOINTMENT_ACTIONS = {
  CANCEL: 'cancel',
  CONFIRM: 'confirm',
  DECLINE: 'decline',
};
const ServiceRequestDescriptions: React.FC<ServiceRequestDescriptionsProps> = ({
                                                                                 appointment = {},
                                                                                 action,
                                                                                 loading,
                                                                                 onClose,
                                                                                 onRefresh,
                                                                                 onActionEnd,
                                                                               }) => {

  const [cancelPopConfirmOpen, setCancelPopConfirmOpen] = useState(false);
  const [confirmPopConfirmOpen, setConfirmPopConfirmOpen] = useState(false);
  const [declinePopConfirmOpen, setDeclinePopConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { canEditServiceRequest } = useServiceRequestPermissions(appointment);

  const ACTION_TO_POP_CONFIRM_MAPPING = {
    [APPOINTMENT_ACTIONS.CANCEL]: setCancelPopConfirmOpen,
    [APPOINTMENT_ACTIONS.DECLINE]: setDeclinePopConfirmOpen,
    [APPOINTMENT_ACTIONS.CONFIRM]: setConfirmPopConfirmOpen,
  };


  const attachedExtraFields = appointment?.service_request_extra_fields?.filter(
    (field) => !isEmpty(field.values),
  );
  const selectedFieldIds = useMemo(() => {
    return attachedExtraFields?.map((field) => field.uuid) || [];
  }, [attachedExtraFields]);
  const handleFieldSelect = async (field: ExtraFieldTypeModel) => {
    try {
      await setExtraFieldValueRequest({
        params: {
          extraFieldTypeId: field.uuid,
          leadId: appointment?.id,
        },
        data: {
          value: field.field_type === 'bool' ? false : null,
        },
      });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
    } finally {
    }
  };
  useEffect(() => {
    if (!action) return;

    const fn = ACTION_TO_POP_CONFIRM_MAPPING[action];
    if (fn) fn();
  }, [action]);

  const handleServiceChange = (serviceId, service) => {
    if (!serviceId || !service) return;

    const assignee = getServiceRequestAssignee(appointment);
    const assigneeId = assignee?.id;
    const assigneeName = [assignee?.firstname, assignee?.lastname].filter(Boolean).join(' ');

    const performChange = () => {
      dispatch({
        type: 'serviceRequest/updateCalendarEvent',
        payload: {
          event: {
            id: appointment?.id,
            branch_service: service,
          },
        },
      });
      editServiceRequestRequest({
        params: { serviceRequestId: appointment?.id },
        data: { branch_service: service.id },
      });
    };

    if (!assigneeId) { performChange(); return; }

    confirmIfAgentServiceMismatch({
      agentId: assigneeId,
      agentName: assigneeName,
      serviceAgents: service.agents || [],
      serviceName: service.service_type?.name || '',
      intl,
      onConfirm: performChange,
    });
  };

  const getBranchInfo = () => {
    const branch = appointment?.branch_service?.branch || appointment?.lead?.branch;
    if (!branch) return null;

    return (
      <div style={{ marginBottom: 8 }}>
        <Space direction="vertical" size={4}>
          <Space>
            <BankOutlined className={styles.icon} />
            {!appointment?.blocker && canEditServiceRequest && isServiceRequestStatusEditable(appointment) ? (
              <BranchServiceSelect
                value={appointment?.branch_service?.id}
                branchId={appointment?.branch_service?.branch?.id}
                onChange={handleServiceChange}
                selectProps={{ variant: 'borderless', style: { minWidth: 200 } }}
              />
            ) : (
              <Space size={4}>
                <BranchActionTag branch={branch} />
                {appointment?.branch_service?.service_type?.name && (
                  <>
                    <RightOutlined className={styles.branchSeparator} />
                    <span>{appointment.branch_service.service_type.name}</span>
                  </>
                )}
              </Space>
            )}
          </Space>
          {branch.offering?.name && (
            <Tag className={styles.offeringTag}>{branch.offering.name}</Tag>
          )}
        </Space>
      </div>
    );
  };


  const getAddressInfo = () => {
    const branch = appointment?.branch_service?.branch || appointment?.lead?.branch;
    const address = branch?.address || appointment?.where;

    if (!address) return null;

    return (
      <div>
        <Space>
          <EnvironmentOutlined className={styles.icon} />
          {address}
        </Space>
      </div>
    );
  };

  const getAttendeesInfo = () => {
    const lead = appointment?.lead;

    return (
      <Space direction="vertical" size="small">
        {lead && (
          <Space>
            <UserOutlined className={styles.icon} />
            <F id="entity.lead.singular" defaultMessage="Lead" />
            <LeadActionTag highlightedAppointmentId={appointment?.id} size={'large'} lead={lead} />
          </Space>
        )}
        <Space>
          <UserOutlined className={styles.icon} />
          <F id="entity.agent.singular" defaultMessage="Assignee" />
          <ServiceRequestAssignee
            serviceRequest={appointment}
          />
        </Space>
      </Space>
    );
  };

  const getStatusInfo = () => {
    return appointment?.status && <ServiceRequestStatusTag fontSize={10} status={appointment?.status} />;
  };

  const getExtraFieldsInfo = () => {
    if (appointment?.blocker) return null;

    return (
      <InfoCard
        variant={'appointment'}
        title={<F id={'entity.extra_field.plural'} defaultMessage={"Custom Fields"} />}
        size="small"
      >
        {attachedExtraFields && attachedExtraFields.length > 0 && (
          <Row gutter={8}>
            <Col span={24}>
              <DynamicExtraFieldsForm
                fields={attachedExtraFields}
                serviceRequestId={appointment?.id}
                labelWidth={'100%'}
                formProps={{ layout: 'vertical' }}
                removable
                grid
                onRefresh={onRefresh}
              />
            </Col>
          </Row>
        )}

        <Row gutter={8}>
          <Col span={24}>
            <ExtraFieldSelector
              relatedTo="serviceRequest"
              selectedFieldIds={selectedFieldIds}
              onFieldSelect={handleFieldSelect}
              onFieldCreated={handleFieldSelect}
            />
          </Col>
        </Row>
      </InfoCard>
    );
  };

  /**
   * Who created the event — deliberately understated (small, muted, bottom of
   * the drawer next to the id). Only the calendar-event DETAIL response carries
   * `creator`; service requests and the calendar list response never do, so
   * this renders nothing for them.
   */
  const getCreatorInfo = () => {
    const creator = appointment?.creator;
    if (!creator) return null;

    const name =
      [creator.firstname, creator.lastname].filter(Boolean).join(' ') ||
      creator.fullname ||
      creator.email;
    if (!name) return null;

    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        <F id="common.attribute.created_by" defaultMessage="Created by" />
        {': '}
        {name}
      </Text>
    );
  };

  const handleJumpToCalendar = () => {
    if (onClose)
      onClose();
    const field = appointment?.blocker ? 'calendar_event_id' : 'appointment_id';
    navigate(`/calendar?${field}=${appointment?.id}`, '_blank');
  };
  const getActionsInfo = () => {
    return (
      <ServiceRequestAction
        serviceRequest={appointment}
        onActionEnd={onActionEnd || onRefresh}
        size="middle"
        showActionText
        hideJumpAction
        cancelProps={{
          open: cancelPopConfirmOpen,
          onOpenChange: setCancelPopConfirmOpen,
        }}
        confirmProps={{
          open: confirmPopConfirmOpen,
          onOpenChange: setConfirmPopConfirmOpen,
        }}
        declineProps={{
          open: declinePopConfirmOpen,
          onOpenChange: setDeclinePopConfirmOpen,
        }}
      />
    );
  };

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '0 12px', display: 'flex', gap: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Flex justify={'space-between'} align="end" gap={16}>
            {appointment?.blocker ? (
              appointment?.title ? (
                <Tag
                  variant={'solid'}
                  className={styles.calendarEventDrawerTitle}
                  color={appointment?.color}
                >
                  {appointment?.title}
                </Tag>
              ) : (
                ' - '
              )
            ) : (
              <EditableTextField
                isTable
                serviceRequest={appointment}
                property="title"
              >
                {appointment?.title || <Text type="secondary">No title</Text>}
              </EditableTextField>
            )}
            {getStatusInfo()}
          </Flex>
          {appointment?.blocker && appointment?.description && (
            <Card size="small">
              <Text type="secondary">{appointment.description}</Text>
            </Card>
          )}
          <Card size="small">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
              }}
            >
              <Space direction="vertical" size={8}>
                <AppointmentTimeEditor
                  appointment={appointment}
                  onSaved={onRefresh}
                />
                {appointment?.recurring && (
                  <Space>
                    <SyncOutlined />
                    <Text type="secondary">
                      {describeRecurring(appointment.recurring, intl)}
                    </Text>
                  </Space>
                )}
                <div>
                  {getBranchInfo()}
                  {getAddressInfo()}
                  {appointment?.cancellation_reason && (
                    <div style={{ marginTop: 8 }}>
                      <Space>
                        <CloseCircleOutlined className={styles.icon} />
                        <Text type="secondary">
                          <F id="pages.serviceRequests.cancellationReason" defaultMessage="Cancellation Reason" />
                        </Text>
                        <Text>{appointment.cancellation_reason}</Text>
                      </Space>
                    </div>
                  )}
                </div>
              </Space>
              {!appointment?.blocker && (
                <Flex justify={'center'} align={'center'} gap={8}>
                  <Tooltip
                    title={
                      <F
                        id="pages.lead.jumpToDate"
                        defaultMessage="Jump to Calendar"
                      />
                    }
                  >
                    <Button
                      size="small"
                      shape="round"
                      icon={<CalendarOutlined />}
                      onClick={handleJumpToCalendar}
                    />
                  </Tooltip>
                </Flex>
              )}
            </div>
          </Card>

          <Row gutter={[16, 16]} align="stretch">
            <Col span={24}>
              <Card style={{ height: '100%' }} size="small">
                {getAttendeesInfo()}
              </Card>
            </Col>
          </Row>

          {!appointment?.blocker && <AppointmentTasks appointmentId={appointment?.id} />}

          {getExtraFieldsInfo()}
          <div style={{ textAlign: 'right' }}>{getActionsInfo()}</div>
          {getCreatorInfo()}
          <CopyableId id={appointment?.id} />
        </Space>
      </div>
    </Spin>
  );
};

export default ServiceRequestDescriptions;
